import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import ApiService from "../../services/Api.service";
import Dropdown from "../Dropdown/Dropdown";
import Datepicker from "../Datepicker/Datepicker";
import moment from "moment";

const OPERATORS = [">", ">=", "<", "<=", "="];

const CampaignCallbackModel = ({ item, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    campaign_id: item?.id || "",
    disposition: [],
    status: [],
    call_count: 0,
    operator: ">=",
  });

  const [dispositionOptions, setDispositionOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [campaignPresetOptions, setCampaignPresetOptions] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState("Fresh");
  const [loading, setLoading] = useState(false);
  const [loadingCount, setLoadingCount] = useState(false);
  const [callCountError, setCallCountError] = useState("");
  const [leadCount, setLeadCount] = useState(null);
  const [canProceed, setCanProceed] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [error, setError] = useState("");
  const [batchesOptions, setBatchesOptions] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(0);
  

  /* ---------- Fetch Options ---------- */
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const disRes = await ApiService.getDispositionOptions();
        const statusRes = await ApiService.getCallStatusOptions();
        const campaignPresetRes = await ApiService.getCampaignPresetOptions();
        const batchesRes = await ApiService.getBatchesOptions(formData.campaign_id);
        setDispositionOptions(disRes?.data?.data || []);
        setStatusOptions(statusRes?.data?.data || []);
        setCampaignPresetOptions(campaignPresetRes?.data?.data || []);
        setBatchesOptions(batchesRes?.data?.data || []);

      } catch (err) {
        console.error("Error loading options:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const handleStartDateChange = (date) => {
    setStartDate(date);

    // Optional validation
    if (endDate && date > endDate) {
      setError("Start date cannot be after end date");
    } else {
      setError("");
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);

    if (startDate && date < startDate) {
      setError("End date cannot be before start date");
    } else {
      setError("");
    }
  };
  const handleBatchChange = (e) => {
    setSelectedBatch(e.target.value);
  }

  const handleCampaignChange = (e) => {
    setSelectedPreset(e.target.value);
  };
  /* ---------- Checkbox Handler ---------- */
  const handleCheckboxChange = (field, value) => {
    setFormData((prev) => {
      const exists = prev[field].includes(value);
      return {
        ...prev,
        [field]: exists
          ? prev[field].filter((v) => v !== value)
          : [...prev[field], value],
      };
    });

    setLeadCount(null);
    setCanProceed(false);
  };

  useEffect(() => {
    // Do not call API if nothing is selected
    // if (!formData.disposition.length && !formData.status.length ||selectedPreset.lenght) {
    //   setLeadCount(null);
    //   setCanProceed(false);
    //   return;
    // }

    const timeoutId = setTimeout(async () => {
      setLoadingCount(true);
      setLeadCount(null);
      setCanProceed(false);

      try {
        const payload = {
          campaign_id: formData.campaign_id,
          disposition: formData.disposition,
          status: formData.status,
          call_count: Number(formData.call_count),
          operator: formData.operator,
          preset: selectedPreset,
          batch: selectedBatch,
          start_date: startDate
            ? moment(startDate).format("YYYY-MM-DD")
            : null,

          end_date: endDate
            ? moment(endDate).format("YYYY-MM-DD")
            : null,
        };

        const res = await ApiService.getCallbackLeadCount(payload);

        if (res.status === 200) {
          const count = res.data?.count || 0;
          setLeadCount(count);
          setCanProceed(count > 0);
        }
      } catch (error) {
        console.error("Error fetching lead count", error);
      } finally {
        setLoadingCount(false);
      }
    }, 600);

    return () => clearTimeout(timeoutId);
  }, [
    formData.disposition,
    formData.status,
    formData.call_count,
    formData.operator,
    selectedBatch,
    selectedPreset,
    endDate,
    startDate
  ]);


  /* ---------- Save (Re-Churn) ---------- */
  const handleSave = async () => {
    if (!canProceed) return;

    try {
      const payload = {
        campaign_id: formData.campaign_id,
        disposition: formData.disposition,
        status: formData.status,
        call_count: Number(formData.call_count),
        operator: formData.operator,
        preset: selectedPreset,
        batch: selectedBatch,
        start_date: startDate
          ? moment(startDate).format("YYYY-MM-DD")
          : null,

        end_date: endDate
          ? moment(endDate).format("YYYY-MM-DD")
          : null,
      };

      const res = await ApiService.updateLeadCallback(payload);

      if (res.status === 200) {
        onSave();   // refresh parent / success callback
        onClose();  // close modal
      }
    } catch (error) {
      console.error("Error saving callback", error);
    }
  };


  return (
    <div className="add-subparam-overlay">
      <div className="add-subparam-modal campaign-modal">
        {/* Header */}
        <div className="add-subparam-header">
          <h3>Lead Call Back Filters</h3>
          <button onClick={onClose} className="close-btn">
            <X size={20} />
          </button>
        </div>
        <div className="callback-filter-container">
          <Dropdown
            name="campaign_preset"
            id="campaign_preset"
            placeholder="Select Campaign Preset"
            value={selectedPreset}
            onChange={handleCampaignChange}
            options={campaignPresetOptions}
            selectLabelEnabled
            selectLabel="Select Campaign Preset"
          />
          <Dropdown
            name="batches"
            id="batches"
            placeholder="Select Batch"
            value={selectedBatch}
            onChange={handleBatchChange}
            options={batchesOptions}
            selectLabelEnabled
            selectLabel="Select Batch"
          />

          <Datepicker
            name="start_date"
            value={startDate}
            onChange={handleStartDateChange}
            dateFormat="dd-MM-yyyy"
            placeholderText="Select Start Date"
            errorMessage={error}
            errorClass={error ? true : false}
          />

          <Datepicker
            name="end_date"
            value={endDate}
            onChange={handleEndDateChange}
            dateFormat="dd-MM-yyyy"
            placeholderText="Select End Date"
            errorMessage={error}
            errorClass={error ? true : false}
          />
        </div>


        {/* Body */}
        <div className="callback-body">
          <div className="parent-filter-section">
            {/* Disposition */}
            {selectedPreset === "Custom" && (
              <>
                <div className="filter-section">
                  <h4>Disposition</h4>
                  {dispositionOptions.map((d) => (
                    <label key={d.value} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.disposition.includes(d.value)}
                        onChange={() =>
                          handleCheckboxChange("disposition", d.value)
                        }
                      />
                      {d.label}
                    </label>
                  ))}
                </div>

                {/* Status */}
                <div className="filter-section">
                  <h4>Status</h4>
                  {statusOptions.map((s) => (
                    <label key={s.value} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.status.includes(s.value)}
                        onChange={() =>
                          handleCheckboxChange("status", s.value)
                        }
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </>
            )
            }

            {/* Call Count */}
            <div className="filter-section">
              <h4>Call Count</h4>
              <div className="call-count-row">
                <select
                  value={formData.operator}
                  onChange={(e) =>
                    setFormData({ ...formData, operator: e.target.value })
                  }
                >
                  {OPERATORS.map((op) => (
                    <option key={op} value={op}>
                      {op}
                    </option>
                  ))}
                </select>

                <input
                  type="number"
                  min={0}
                  value={formData.call_count}
                  onChange={(e) => {
                    const value = e.target.value;

                    if (value === "") {
                      setFormData({ ...formData, call_count: "" });
                      setCallCountError("Please enter a number");
                    } else if (Number(value) < 1) {
                      setFormData({ ...formData, call_count: value });
                      setCallCountError("Value must be at least 1");
                    } else {
                      setFormData({ ...formData, call_count: Number(value) });
                      setCallCountError("");
                    }
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "" || Number(e.target.value) < 1) {
                      setFormData({ ...formData, call_count: 0 }); // fallback
                      setCallCountError("");
                    }
                  }}
                />


              </div>
              {callCountError && (
                <p style={{ color: "red" }}>{callCountError}</p>
              )}
            </div>
            {/* Actions */}
            {loadingCount && (
              <div className="filter-section">
                <p>Checking matching leads...</p>
              </div>
            )}

            {/* Matching Leads */}
            {leadCount !== null && (
              <div className="filter-section">
                <h4>Matching Leads</h4>
                <p className="lead-count-text">
                  Count: <strong>{leadCount}</strong>
                </p>
              </div>
            )}
          </div>



        </div>

        {/* Footer */}
        <div className="add-subparam-footer">
          <button className="popup-cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="popup-add-btn"
            onClick={handleSave}
            disabled={!canProceed}
          >
            Start
          </button>
        </div>
      </div>
    </div>
  );
};

export default CampaignCallbackModel;
